import { notification } from 'antd';
import links from 'helpers/links';
import translate from 'languageProvider/inline';
import Nprogress from 'nprogress';
import { matchPath } from 'react-router';
import { all, call, cancelled, put, select, spawn, takeLatest } from 'redux-saga/effects';
import applicationActions from 'redux/application/actions';
import { fetchApplicationGraphql } from 'redux/application/graphql';
import { fetchFlagsFinished } from 'redux/featureFlags';
import { ReduxAction } from 'types/redux.types';
import createApplicationActions from '../editApplication/actions';
import projectActions from './actions';
import fetchProjectGraphql, {
  fetchApplicationFiltersGql,
  fetchProjectPPPGraphql,
  fetchProjectPPPLockedApplicationsGraphql,
  fetchProjectSmartSortProcessingStatusGraphql,
  fetchSelectionLevelConfigGraphql,
  mutateCustomSelectionLabelsGraphql,
} from './graphql';

function* fetchProjectGenerator(action: ReduxAction): any {
  const projectState = yield select((state: any) => state.Project);
  try {
    yield call(() => {
      Nprogress.start();
    });

    const activeSorts = projectState
      .get('sort')
      .filter((sortDirection: any) => sortDirection !== null);

    const selectedFilters = projectState.get('selectedFilters').toJS();

    const ratingFilterByEmployer = selectedFilters.rating.find((rating: string) =>
      rating.includes('employer-score')
    );
    if (ratingFilterByEmployer) {
      selectedFilters.rating = [];
    }

    const ratingDefaultSort = ratingFilterByEmployer ? 'desc' : undefined;

    const sort = {
      sortField: ratingFilterByEmployer ?? Object.keys(activeSorts.toJS())[0],
      sortDirection: activeSorts.toJS()[Object.keys(activeSorts.toJS())[0]] ?? ratingDefaultSort,
    };

    const paths = projectState.get('preselectionTreeSelect').get('paths');

    const payload = {
      ...action.payload,
      variables: {
        ...action.payload.variables,
        ...selectedFilters,
        preselectionFilter: paths,
        ...sort,
      },
    };

    const response = yield call(fetchProjectGraphql, payload);
    yield put({
      type: projectActions.FETCH_PROJECT_DONE,
      payload: response.data.project,
    });
  } catch (e: any) {
    if (e.message.indexOf('not.visible') !== -1) {
      yield put({ type: projectActions.FETCH_PROJECT_NOT_VISIBLE });
    } else if (e.message.indexOf('access.denied') !== -1) {
      yield put({ type: projectActions.FETCH_PROJECT_DENIED });
    } else {
      notification.error({
        title: translate('error.userError'),
        description: translate('error.tryAgain'),
        duration: 0,
      });
    }
    yield put({
      type: projectActions.FETCH_PROJECT_ERROR,
      payload: e.message,
    });
  } finally {
    yield call(() => {
      Nprogress.done();
    });
    if (yield cancelled()) {
      yield put({
        type: projectActions.FETCH_PROJECT_CANCELLED,
      });
    }
  }
}

function* selectDefaultFilterGenerator(): any {
  const projectState = yield select((state: any) => state.Project);

  const filters = projectState.get('applicationFilters');
  const areFiltersDirty = projectState.get('filtersDirty');
  const featureFlags = yield select((state: any) => state.FeatureFlags.flags);

  const pathname: string = yield select((state: any) => state.router.location?.pathname);

  const match: any = matchPath(pathname, {
    path: links.application().pattern,
    exact: false,
    strict: false,
  });

  const projectId = match?.params?.projectId ? parseInt(match.params.projectId, 10) : null;
  const applicationId = match?.params?.applicationId
    ? parseInt(match.params.applicationId, 10)
    : null;

  const filtersLoaded = filters && typeof filters === 'object' && Object.keys(filters).length > 0;

  if (!areFiltersDirty && projectId && !applicationId && featureFlags !== null && filtersLoaded) {
    /** setup default filter here */
    // yield put({
    //   type: projectActions.SET_FILTER,
    //   payload: {
    //     group: 'selectionLevel',
    //     filters: SelectionLevel.Interview,
    //     variables: {
    //       id: projectId,
    //     },
    //     recache: true,
    //   },
    // });
  }
}

/* fetchApplicationFiltersGenerator */
function* fetchApplicationFiltersGenerator(action: ReduxAction): any {
  try {
    let id = null;

    if (action.type === projectActions.APPLICATION_FILTER_FETCH) {
      id = action.payload;
    }

    if (!id) {
      const projectState = yield select((state: any) => state.Project);
      id = projectState.get('currentProject').get('id');
    }

    yield call(() => {
      Nprogress.start();
    });
    const response = yield call(fetchApplicationFiltersGql, id);
    yield put({
      type: projectActions.APPLICATION_FILTER_FETCHING_DONE,
      payload: response.data.applicationFilters,
    });
  } catch (e) {
    log(e);
    yield put({ type: projectActions.APPLICATION_FILTER_FETCHING_ERROR });
  } finally {
    yield call(() => {
      Nprogress.done();
    });
    if (yield cancelled()) {
      yield put({
        type: projectActions.APPLICATION_FILTER_FETCHING_CANCELLED,
      });
    }
  }
}

function* setApplicationToSelectionGenerator(): any {
  const projectState = yield select((state: any) => state.Project);
  const selectCount = Object.keys(projectState.toJS().selectedApplications).length;
  yield put({
    type: projectActions.TOGGLE_BULK_ACTIONS,
    payload: {
      state: !!selectCount,
    },
  });
}

function* mutateCustomSelectionLabelsGenerator(action: ReduxAction): any {
  try {
    yield call(() => {
      Nprogress.start();
    });

    const response = yield call(mutateCustomSelectionLabelsGraphql, action.payload);
    yield put({
      type: projectActions.SAVE_CUSTOM_SELECTION_LABELS_DONE,
      payload: response.data.projectCustomSelectionLabels,
    });

    // A brand-new level is sent without a `selectionLevelValue` (the backend assigns one on create).
    // The mutation response doesn't map that new value back onto the drawer's optimistic rows, so on a
    // no-reload reopen the row stays flagged as unsaved (`isNew`, no persisted level) — which, among
    // other things, makes it delete without the confirmation step that persisted levels require.
    // Re-pull the authoritative selection-level config so the row reconciles to its persisted identity,
    // exactly as a full page reload would. Only needed when a level was actually created.
    const savedRows = action.payload?.variables?.customSelectionLabel;
    const createdNewLevel =
      Array.isArray(savedRows) && savedRows.some((r: any) => r?.selectionLevelValue == null);
    const projectId = action.payload?.variables?.projectId;
    if (createdNewLevel && projectId) {
      yield put({
        type: projectActions.FETCH_SELECTION_LEVEL_CONFIG_START,
        payload: { variables: { id: projectId } },
      });
    }
  } catch (e: any) {
    log(e);
    // The backend refuses to delete a selection level that still holds applications (a race-safe
    // backstop for the FE guard that already disables delete on non-empty columns) — surface that
    // specific reason instead of the generic retry message.
    const errorMessage = e?.graphQLErrors?.[0]?.message ?? e?.message ?? '';
    const deleteBlocked = String(errorMessage).includes('cannot_delete_with_applications');
    notification.error({
      title: translate('error.userError'),
      description: deleteBlocked
        ? translate('selectionLevels.editor.delete.rejected')
        : translate('error.tryAgain'),
      duration: 0,
    });
    // Restore authoritative config so the optimistic (now-failed) label/visibility edits are undone.
    const projectId = action.payload?.variables?.projectId;
    if (projectId) {
      yield put({
        type: projectActions.FETCH_SELECTION_LEVEL_CONFIG_START,
        payload: { variables: { id: projectId } },
      });
    }
    yield put({ type: projectActions.SAVE_CUSTOM_SELECTION_LABELS_ERROR });
  } finally {
    yield call(() => {
      Nprogress.done();
    });
    if (yield cancelled()) {
      yield put({
        type: projectActions.SAVE_CUSTOM_SELECTION_LABELS_CANCELLED,
      });
    }
  }
}

function* fetchProject() {
  yield takeLatest(
    [
      projectActions.FETCH_PROJECT_START,
      projectActions.SET_FILTER,
      projectActions.RESET_FILTER,
      projectActions.SET_SORT,
    ],
    fetchProjectGenerator
  );
}

export function* setApplicationToSelection() {
  yield takeLatest(
    [projectActions.MODIFY_APPLICATION_SELECTION, projectActions.RESET_APPLICATION_SELECTION],
    setApplicationToSelectionGenerator
  );
}

/**
 * fetch application filters
 */
export function* fetchApplicationFilters() {
  yield takeLatest(
    [
      projectActions.APPLICATION_FILTER_FETCH,
      createApplicationActions.CREATE_APPLICATION_DONE,
      applicationActions.APPLICATION_DELETE_DONE,
      applicationActions.APPLICATION_TAGS_FETCH_DONE,
      applicationActions.APPLICATION_EVENTS_FETCH_DONE,
      applicationActions.APPLICATION_RATING_FETCH_DONE,
    ],
    fetchApplicationFiltersGenerator
  );
}

function* mutateCustomSelectionLabels() {
  yield takeLatest(
    projectActions.SAVE_CUSTOM_SELECTION_LABELS_START,
    mutateCustomSelectionLabelsGenerator
  );
}

function* fetchProjectPPPGenerator(action: ReduxAction): any {
  try {
    yield call(() => {
      Nprogress.start();
    });

    const response = yield call(fetchProjectPPPGraphql, action.payload.projectId);
    yield put({
      type: projectActions.FETCH_PROJECT_PPP_DONE,
      payload: response.data.project,
    });

    if (response.data.project.projectHasJobWithPPP) {
      yield put({
        type: projectActions.FETCH_PROJECT_PPP_LOCKED_APPLICATIONS_START,
        payload: {
          projectId: action.payload.projectId,
        },
      });
    } else {
      yield put({
        type: projectActions.FETCH_PROJECT_PPP_LOCKED_APPLICATIONS_CANCELLED,
        payload: response.data.project,
      });
    }
  } catch (e: any) {
    notification.error({
      title: translate('error.userError'),
      description: translate('error.tryAgain'),
      duration: 0,
    });
    yield put({
      type: projectActions.FETCH_PROJECT_PPP_ERROR,
      payload: e.message,
    });
  } finally {
    yield call(() => {
      Nprogress.done();
    });
    if (yield cancelled()) {
      yield put({
        type: projectActions.FETCH_PROJECT_PPP_CANCELLED,
      });
    }
  }
}

function* fetchProjectPPPLockedApplicationsGenerator(action: ReduxAction): any {
  try {
    yield call(() => {
      Nprogress.start();
    });

    const response = yield call(fetchProjectPPPLockedApplicationsGraphql, action.payload.projectId);
    yield put({
      type: projectActions.FETCH_PROJECT_PPP_LOCKED_APPLICATIONS_DONE,
      payload: response.data.project,
    });
  } catch (e: any) {
    notification.error({
      title: translate('error.userError'),
      description: translate('error.tryAgain'),
      duration: 0,
    });
    yield put({
      type: projectActions.FETCH_PROJECT_PPP_LOCKED_APPLICATIONS_ERROR,
      payload: e.message,
    });
  } finally {
    yield call(() => {
      Nprogress.done();
    });
    if (yield cancelled()) {
      yield put({
        type: projectActions.FETCH_PROJECT_PPP_LOCKED_APPLICATIONS_CANCELLED,
      });
    }
  }
}

function* fetchApplicationRelevanceGenerator(action: ReduxAction): any {
  try {
    const response = yield call(fetchApplicationGraphql, {
      variables: action.payload,
      recache: true,
    });
    yield put({
      type: projectActions.APPLICATION_RELEVANCE_REFRESH_DONE,
      payload: response.data.application,
    });
  } catch (e: any) {
    log(e);
  }
}

function* fetchProjectSmartSortProcessingStatusGenerator(action: ReduxAction): any {
  try {
    const response = yield call(
      fetchProjectSmartSortProcessingStatusGraphql,
      action.payload.projectId
    );
    yield put({
      type: projectActions.PROJECT_SMART_SORT_PROCESSING_REFETCH_DONE,
      payload: response.data.project,
    });
  } catch (e: any) {
    log(e);
  }
}

function* fetchProjectPPP() {
  yield takeLatest(projectActions.FETCH_PROJECT_PPP_START, fetchProjectPPPGenerator);
}

function* fetchProjectPPPLockedApplications() {
  yield takeLatest(
    projectActions.FETCH_PROJECT_PPP_LOCKED_APPLICATIONS_START,
    fetchProjectPPPLockedApplicationsGenerator
  );
}

function* fetchApplicationRelevance() {
  yield takeLatest(
    projectActions.APPLICATION_RELEVANCE_REFRESH_START,
    fetchApplicationRelevanceGenerator
  );
}

function* fetchProjectSmartSortProcessingStatus() {
  yield takeLatest(
    projectActions.APPLICATION_RELEVANCE_REFRESH_START,
    fetchProjectSmartSortProcessingStatusGenerator
  );
}

function* selectDefaultFilter() {
  yield takeLatest(
    [projectActions.APPLICATION_FILTER_FETCHING_DONE, fetchFlagsFinished],
    selectDefaultFilterGenerator
  );
}

function* fetchSelectionLevelConfigGenerator(action: ReduxAction): any {
  try {
    const response = yield call(fetchSelectionLevelConfigGraphql, action.payload);
    yield put({
      type: projectActions.FETCH_SELECTION_LEVEL_CONFIG_DONE,
      payload: response.data.project,
    });
  } catch (e: any) {
    log(e);
    yield put({ type: projectActions.FETCH_SELECTION_LEVEL_CONFIG_ERROR });
  }
}

function* fetchSelectionLevelConfig() {
  yield takeLatest(
    projectActions.FETCH_SELECTION_LEVEL_CONFIG_START,
    fetchSelectionLevelConfigGenerator
  );
}

export default function* rootSaga() {
  yield all([
    spawn(fetchProject),
    spawn(fetchApplicationFilters),
    spawn(setApplicationToSelection),
    spawn(mutateCustomSelectionLabels),
    spawn(fetchProjectPPP),
    spawn(fetchProjectPPPLockedApplications),
    spawn(fetchApplicationRelevance),
    spawn(fetchProjectSmartSortProcessingStatus),
    spawn(selectDefaultFilter),
    spawn(fetchSelectionLevelConfig),
  ]);
}
